import Clipboard from 'clipboard'
import { ElMessage } from 'element-plus'
import i18n from '@/locales'

/**
 * 检测是否支持 Clipboard.js 方式
 */
const isClipboardJSSupported = (): boolean => {
  return typeof Clipboard !== 'undefined'
}

/**
 * 检测是否支持 execCommand
 */
const isExecCommandSupported = (): boolean => {
  return document.queryCommandSupported && document.queryCommandSupported('copy')
}

export const clipboardSuccess = () => {
  const message = i18n.global.t('common.copySuccess')
  ElMessage({
    message: message,
    type: 'success',
    duration: 2000
  })
}

export const clipboardError = (error: string | null) => {
  const message = error ? i18n.global.t('common.copyError') + ',' + error : i18n.global.t('common.copyError')
  ElMessage({
    message: message,
    type: 'error',
    duration: 2000
  })
}

// 提取错误信息
const getErrorMessage = (error: any): string => {
  // 如果是 Clipboard.js 的错误
  if (error && error.message) {
    return error.message
  }

  // 检查是否是权限错误
  if (error && error.name === 'NotAllowedError') {
    return '没有剪贴板权限，请检查浏览器设置'
  }

  // 检查是否是安全上下文错误
  if (!window.isSecureContext) {
    return '当前环境不支持自动复制（需要 HTTPS 或 localhost）'
  }

  // 检查是否支持 Clipboard API
  if (!navigator.clipboard) {
    return '当前浏览器不支持剪贴板功能'
  }

  return '请手动复制'
}

export const handleClipboard = (text: string, event: MouseEvent) => {
  const clipboard = new Clipboard(event.target as Element, {
    text: () => text
  })
  clipboard.on('success', () => {
    clipboardSuccess()
    clipboard.destroy()
  })
  clipboard.on('error', (err) => {
    // 输出具体错误信息
    console.error('剪贴板复制失败:', err)

    // 获取错误详情
    const errorMessage = getErrorMessage(err)
    clipboardError(errorMessage)
    clipboard.destroy()
  });
  (clipboard as any).onClick(event)
}

/**
 * 使用 execCommand 复制（备选方案）
 */
const copyWithExecCommand = (text: string) => {
  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.top = '-9999px'
    textarea.style.left = '-9999px'
    textarea.setAttribute('readonly', '')
    document.body.appendChild(textarea)

    textarea.select()
    textarea.setSelectionRange(0, text.length)

    const success = document.execCommand('copy')
    document.body.removeChild(textarea)
    if (success) {
      clipboardSuccess()
    } else {
      clipboardError(null)
    }
  } catch (error) {
    console.error('execCommand 复制失败:', error)
    const errorStr = getErrorMessage(error)
    clipboardError(errorStr)
  }
}
/**
 * 智能复制（自动选择最佳方法）
 */
export const intelligentClipboard = async(text: string, event?: MouseEvent) => {
  if (!text) {
    clipboardError('复制内容为空')
    return
  }

  // 优先使用 Clipboard.js（如果提供了 event）
  if (event && isClipboardJSSupported()) {
    handleClipboard(text, event)
  } else if (isExecCommandSupported()) {
    // 降级使用 execCommand
    copyWithExecCommand(text)
  } else {
    // 所有方法都失败
    clipboardError('请手动复制')
  }
}
